/**
 * BulkShiftModal — create a recurring shift pattern in one shot.
 *
 * Tick the days of the week, set a date range and a time window, pick the
 * worker, and (optionally) link the visit to a service user. The backend
 * materialises every instance and returns which ones it skipped due to
 * conflicts. The user can override-and-retry if they truly want to force.
 */

import { useState, useMemo } from "react";
import { Loader2, X, Repeat, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { fmtISODate } from "@/lib/dates";

const WEEKDAYS = [
  { idx: 0, label: "Mon" },
  { idx: 1, label: "Tue" },
  { idx: 2, label: "Wed" },
  { idx: 3, label: "Thu" },
  { idx: 4, label: "Fri" },
  { idx: 5, label: "Sat" },
  { idx: 6, label: "Sun" },
];
const SHIFT_TYPES = ["Regular Visit", "Sleep-In", "Double-Up", "Cover Visit", "Training", "Standby", "Office"];

export default function BulkShiftModal({
  pivot,
  lockedEntity,   // SU or worker the calendar is focused on
  staff = [],
  users = [],
  defaultStartDate,
  onClose,
  onCreated,
}) {
  // For SU pivot: SU is locked, the user picks a worker. Vice versa for worker pivot.
  const today = fmtISODate(new Date());
  const _initWorker = pivot === "worker" ? lockedEntity.id : "";
  const _initSu = pivot === "service_user" ? lockedEntity.id : "";
  const [workerId, setWorkerId] = useState(_initWorker);
  const [suId, setSuId] = useState(_initSu);
  const [weekdayMask, setWeekdayMask] = useState([0, 2, 4]); // Mon/Wed/Fri default
  const [startDate, setStartDate] = useState(defaultStartDate || today);
  const [endDate, setEndDate] = useState(() => {
    // Default to 4-week window from start.
    const d = new Date(`${defaultStartDate || today}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 27);
    return d.toISOString().slice(0, 10);
  });
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("13:00");
  const [overnight, setOvernight] = useState(false);
  const [shiftType, setShiftType] = useState("Regular Visit");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [overrideOnRetry, setOverrideOnRetry] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const eligibleWorkers = useMemo(
    () => staff
      .filter((s) => ["care_worker", "bank_worker", "team_leader", "registered_manager", "deputy_manager"].includes(s.role))
      .filter((s) => (s.status || "active") === "active")
      .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || "")),
    [staff],
  );

  const toggleDay = (idx) => {
    setWeekdayMask((m) => (m.includes(idx) ? m.filter((x) => x !== idx) : [...m, idx].sort()));
  };

  const totalInstances = useMemo(() => {
    try {
      const a = new Date(`${startDate}T12:00:00Z`);
      const b = new Date(`${endDate}T12:00:00Z`);
      if (b < a) return 0;
      let count = 0;
      for (let d = new Date(a); d <= b; d.setUTCDate(d.getUTCDate() + 1)) {
        // JS getUTCDay: 0=Sun..6=Sat — convert to Mon=0..Sun=6
        const wd = (d.getUTCDay() + 6) % 7;
        if (weekdayMask.includes(wd)) count++;
      }
      return count;
    } catch { return 0; }
  }, [startDate, endDate, weekdayMask]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!workerId) { setError("Please pick a worker."); return; }
    if (!suId) { setError("Please pick a service user."); return; }
    if (!weekdayMask.length) { setError("Select at least one day of the week."); return; }
    if (startDate > endDate) { setError("End date must be on or after start date."); return; }
    setBusy(true);
    try {
      const payload = {
        worker_id: workerId,
        weekday_mask: weekdayMask,
        start_date: startDate,
        end_date: endDate,
        start_time: startTime,
        end_time: endTime,
        overnight,
        shift_type: shiftType,
        status: "published",
        visit_slots: [{ service_user_id: suId, planned_start: startTime, planned_end: endTime }],
        override_conflicts: overrideOnRetry,
        override_reason: overrideReason,
      };
      const { data } = await api.post("/shifts/bulk", payload);
      setResult(data);
      onCreated?.(data.created || []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Failed to create shifts.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="bulk-shift-modal"
      >
        <div className="flex items-start justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 inline-flex items-center gap-2"><Repeat className="h-4 w-4 text-[#0D7377]" /> Bulk add — recurring shift pattern</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {pivot === "service_user"
                ? <>Default service user: <strong className="text-slate-800">{lockedEntity.label}</strong> — change below if needed.</>
                : <>Default worker: <strong className="text-slate-800">{lockedEntity.label}</strong> — change below if needed.</>}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md hover:bg-slate-100" data-testid="bulk-shift-close">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {!result && (
          <div className="px-5 py-4 space-y-4">
            {/* Worker + Service user pickers — both shown so a manager can
               override the calendar's pivot (e.g. open from Tyler's calendar
               but switch SU to a different person). The relevant one is
               pre-populated from `lockedEntity`. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Worker *</span>
                <select required value={workerId} onChange={(e) => setWorkerId(e.target.value)} data-testid="bulk-shift-worker" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                  <option value="">Pick a worker…</option>
                  {eligibleWorkers.map((s) => <option key={s.id} value={s.id}>{s.full_name} ({(s.role || "").replace(/_/g, " ")})</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Service user *</span>
                <select required value={suId} onChange={(e) => setSuId(e.target.value)} data-testid="bulk-shift-su" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                  <option value="">Pick a service user…</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
              </label>
            </div>

            {/* Day-of-week pills */}
            <div>
              <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Days of the week</span>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="bulk-shift-weekday-row">
                {WEEKDAYS.map((d) => {
                  const on = weekdayMask.includes(d.idx);
                  return (
                    <button
                      type="button"
                      key={d.idx}
                      onClick={() => toggleDay(d.idx)}
                      data-testid={`bulk-shift-day-${d.idx}`}
                      className={`px-3 py-1.5 text-xs rounded-md border ${on ? "bg-[#0D7377] text-white border-[#0A5C5F]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Dates + times */}
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Start date</span>
                <input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="bulk-shift-start-date" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">End date</span>
                <input type="date" required value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="bulk-shift-end-date" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Start time</span>
                <input type="time" required value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="bulk-shift-start-time" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">End time</span>
                <input type="time" required value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="bulk-shift-end-time" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3 items-end">
              <label className="block">
                <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Visit type</span>
                <select value={shiftType} onChange={(e) => setShiftType(e.target.value)} data-testid="bulk-shift-type" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                  {SHIFT_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 pb-2">
                <input type="checkbox" checked={overnight} onChange={(e) => setOvernight(e.target.checked)} data-testid="bulk-shift-overnight" />
                Overnight (rolls to next day)
              </label>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm text-emerald-900">
              Will create <strong data-testid="bulk-shift-count">{totalInstances}</strong> shift{totalInstances === 1 ? "" : "s"} between <strong>{startDate}</strong> and <strong>{endDate}</strong>. Per-instance conflict checks (11 h rest, 48 h cap, double-booking, DBS) run server-side.
            </div>

            {error && <p className="text-sm text-red-700" data-testid="bulk-shift-error">{error}</p>}
          </div>
        )}

        {result && (
          <div className="px-5 py-4 space-y-3" data-testid="bulk-shift-result">
            <p className="text-emerald-700 inline-flex items-center gap-2 font-medium"><CheckCircle2 className="h-5 w-5" /> Created <strong>{result.created_count}</strong> shift{result.created_count === 1 ? "" : "s"}.</p>
            {result.skipped_count > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-sm font-semibold text-amber-900 mb-1">{result.skipped_count} skipped due to conflicts</p>
                <ul className="text-xs text-amber-900 max-h-40 overflow-y-auto space-y-1">
                  {result.skipped.map((s, i) => (
                    <li key={i}><strong>{s.date}</strong> · {(s.conflicts || []).map((c) => c.message).join("; ")}</li>
                  ))}
                </ul>
                <label className="flex items-start gap-2 text-xs text-amber-900 mt-2 pt-2 border-t border-amber-200">
                  <input type="checkbox" checked={overrideOnRetry} onChange={(e) => setOverrideOnRetry(e.target.checked)} data-testid="bulk-shift-override" />
                  Retry skipped dates with override (audit-logged)
                </label>
                {overrideOnRetry && (
                  <input required placeholder="Reason for override" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} className="mt-1 w-full px-2 py-1 border border-amber-300 rounded text-xs bg-white" data-testid="bulk-shift-override-reason" />
                )}
                {overrideOnRetry && (
                  <button
                    type="button"
                    onClick={async () => {
                      setBusy(true);
                      try {
                        const skipped_dates = (result.skipped || []).map((s) => s.date);
                        // Re-issue bulk constrained to ONLY the previously-skipped dates by tightening
                        // the range to a synthetic mask. Simpler: send the same payload with override_conflicts=true.
                        const { data } = await api.post("/shifts/bulk", {
                          worker_id: workerId, weekday_mask: weekdayMask,
                          start_date: skipped_dates[0], end_date: skipped_dates[skipped_dates.length - 1],
                          start_time: startTime, end_time: endTime, overnight,
                          shift_type: shiftType, status: "published",
                          visit_slots: [{ service_user_id: suId, planned_start: startTime, planned_end: endTime }],
                          override_conflicts: true, override_reason: overrideReason,
                        });
                        setResult({ created_count: (result.created_count + data.created_count), skipped_count: data.skipped_count, created: [...(result.created || []), ...(data.created || [])], skipped: data.skipped });
                        onCreated?.(data.created || []);
                      } finally { setBusy(false); }
                    }}
                    disabled={busy || !overrideReason.trim()}
                    className="mt-2 px-3 py-1.5 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded-md font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid="bulk-shift-retry-override"
                  >
                    {busy && <Loader2 className="h-3 w-3 animate-spin" />} Retry with override
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-md hover:bg-white">{result ? "Close" : "Cancel"}</button>
          {!result && (
            <button type="submit" disabled={busy || totalInstances === 0} data-testid="bulk-shift-save" className="px-4 py-2 text-sm bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded-md font-medium inline-flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Repeat className="h-4 w-4" />}
              Create {totalInstances} shift{totalInstances === 1 ? "" : "s"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
